import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  FiBox,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiChevronDown,
  FiCheck,
  FiX,
  FiAlertTriangle,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { AddInventoryItemModal } from '../projects/modals/AddInventoryItemModal';
import { EditInventoryItemModal } from '../projects/modals/EditInventoryItemModal';
import {
  useGetProfileInventoryItems,
  useCreateProfileInventoryItem,
  useUpdateProfileInventoryItem,
  useDeleteProfileInventoryItem,
  mapUiCategoryToApi,
  mapApiCategoryToUi,
  mapUiConditionToApi,
  mapApiConditionToUi,
} from '../../api/inventory';
import type { ProfileInventoryItem } from '../../api/inventory';

import { extractApiError } from '../../utils/apiError';
interface InventoryTabProps {
  isOwn: boolean;
}

export const InventoryTab: React.FC<InventoryTabProps> = ({ isOwn }) => {
  const { data, isLoading } = useGetProfileInventoryItems({ page: 1, page_size: 50 });

  // Mutations
  const { mutate: createItem, isPending: isCreating } = useCreateProfileInventoryItem();
  const { mutate: updateItem, isPending: isUpdating } = useUpdateProfileInventoryItem();
  const { mutate: deleteItem, isPending: isDeleting } = useDeleteProfileInventoryItem();

  // Filters State
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isConditionFilterOpen, setIsConditionFilterOpen] = useState(false);
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);

  const conditionRef = useRef<HTMLDivElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      if (conditionRef.current && !conditionRef.current.contains(e.target as Node)) {
        setIsConditionFilterOpen(false);
      }
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  // Modal states
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Form states (Add)
  const [itemName, setItemName] = useState('');
  const [itemCategory, setItemCategory] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [itemAssetTag, setItemAssetTag] = useState('');
  const [itemQuantity, setItemQuantity] = useState('1');
  const [itemBudgetAllocation, setItemBudgetAllocation] = useState('');
  const [itemLocation, setItemLocation] = useState('');
  const [itemCondition, setItemCondition] = useState<
    'Excellent' | 'Good' | 'Fair' | 'Needs Repair'
  >('Excellent');
  const [addError, setAddError] = useState('');

  // Form states (Edit)
  const [editingItem, setEditingItem] = useState<ProfileInventoryItem | null>(null);
  const [editItemName, setEditItemName] = useState('');
  const [editItemCategory, setEditItemCategory] = useState('');
  const [isEditCategoryDropdownOpen, setIsEditCategoryDropdownOpen] = useState(false);
  const [editItemAssetTag, setEditItemAssetTag] = useState('');
  const [editItemQuantity, setEditItemQuantity] = useState('1');
  const [editItemBudgetAllocation, setEditItemBudgetAllocation] = useState('');
  const [editItemLocation, setEditItemLocation] = useState('');
  const [editItemCondition, setEditItemCondition] = useState<
    'Excellent' | 'Good' | 'Fair' | 'Needs Repair'
  >('Excellent');
  const [editError, setEditError] = useState('');

  // Form states (Delete)
  const [deletingItem, setDeletingItem] = useState<ProfileInventoryItem | null>(null);

  const items = data?.results ?? [];

  // Get unique categories for filters
  const uniqueCategories = useMemo(() => {
    const cats = new Set<string>();
    items.forEach((item) => {
      const label = item.category_label || mapApiCategoryToUi(item.category);
      if (label) cats.add(label);
    });
    return Array.from(cats);
  }, [items]);

  // Filter items
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const uiCondition = mapApiConditionToUi(item.condition);
      const uiCategory = item.category_label || mapApiCategoryToUi(item.category);

      const conditionMatch =
        selectedConditions.length === 0 || selectedConditions.includes(uiCondition);
      const categoryMatch =
        selectedCategories.length === 0 || selectedCategories.includes(uiCategory);

      return conditionMatch && categoryMatch;
    });
  }, [items, selectedConditions, selectedCategories]);

  // Filters toggles
  const toggleConditionFilter = (cond: string) => {
    setSelectedConditions((prev) =>
      prev.includes(cond) ? prev.filter((c) => c !== cond) : [...prev, cond],
    );
  };

  const toggleCategoryFilter = (cat: string) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  };

  // Submit Add
  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    if (!itemName.trim()) {
      setAddError('Please enter an item name.');
      return;
    }
    if (!itemCategory || !itemCategory.trim()) {
      setAddError('Please select a valid inventory category.');
      return;
    }

    let mappedCategory: string;
    try {
      mappedCategory = mapUiCategoryToApi(itemCategory);
    } catch (err: any) {
      setAddError(extractApiError(err, 'Please select a valid inventory category.'));
      return;
    }

    createItem(
      {
        name: itemName.trim(),
        category: mappedCategory,
        asset_tag: itemAssetTag.trim() || undefined,
        condition: mapUiConditionToApi(itemCondition),
        quantity: parseInt(itemQuantity, 10) || 1,
        location: itemLocation.trim() || undefined,
        budget_allocation: itemBudgetAllocation.trim() || undefined,
      },
      {
        onSuccess: () => {
          setIsAddOpen(false);
          // reset form
          setItemName('');
          setItemCategory('');
          setItemAssetTag('');
          setItemQuantity('1');
          setItemBudgetAllocation('');
          setItemLocation('');
          setItemCondition('Excellent');
        },
        onError: (err: any) => {
          setAddError(extractApiError(err, 'Failed to add item'));
        },
      },
    );
  };

  // Open Edit
  const handleOpenEdit = (item: ProfileInventoryItem) => {
    setEditingItem(item);
    setEditItemName(item.name);
    setEditItemCategory(item.category_label || mapApiCategoryToUi(item.category));
    setEditItemAssetTag(item.asset_tag || '');
    setEditItemQuantity(String(item.quantity));
    setEditItemBudgetAllocation(item.budget_allocation || '');
    setEditItemLocation(item.location || '');
    setEditItemCondition(mapApiConditionToUi(item.condition));
    setEditError('');
    setIsEditOpen(true);
  };

  // Submit Edit
  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEditError('');
    if (!editingItem) return;
    if (!editItemName.trim()) {
      setEditError('Please enter an item name.');
      return;
    }
    if (!editItemCategory || !editItemCategory.trim()) {
      setEditError('Please select a valid inventory category.');
      return;
    }

    let mappedCategory: string;
    try {
      mappedCategory = mapUiCategoryToApi(editItemCategory);
    } catch (err: any) {
      setEditError(extractApiError(err, 'Please select a valid inventory category.'));
      return;
    }

    updateItem(
      {
        itemUuid: editingItem.uuid,
        payload: {
          name: editItemName.trim(),
          category: mappedCategory,
          asset_tag: editItemAssetTag.trim() || undefined,
          condition: mapUiConditionToApi(editItemCondition),
          quantity: parseInt(editItemQuantity, 10) || 1,
          location: editItemLocation.trim() || undefined,
          budget_allocation: editItemBudgetAllocation.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          setIsEditOpen(false);
          setEditingItem(null);
        },
        onError: (err: any) => {
          setEditError(extractApiError(err, 'Failed to update item'));
        },
      },
    );
  };

  // Open Delete
  const handleOpenDelete = (item: ProfileInventoryItem) => {
    setDeletingItem(item);
    setIsDeleteOpen(true);
  };

  // Confirm Delete
  const handleConfirmDelete = () => {
    if (!deletingItem) return;
    deleteItem(deletingItem.uuid, {
      onSuccess: () => {
        setIsDeleteOpen(false);
        setDeletingItem(null);
      },
    });
  };

  const getStatusBadge = (condition: string) => {
    switch (condition) {
      case 'excellent':
        return {
          text: 'Excellent',
          className:
            'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-3 py-1 rounded-full text-[11px] font-semibold',
        };
      case 'good':
        return {
          text: 'Good',
          className:
            'bg-blue-500/10 text-blue-400 border border-blue-500/20 px-3 py-1 rounded-full text-[11px] font-semibold',
        };
      case 'fair':
        return {
          text: 'Fair',
          className:
            'bg-[#241c03] text-[#fbbf24] border border-[#d97706]/30 px-3 py-1 rounded-full text-[11px] font-semibold',
        };
      case 'needs_repair':
        return {
          text: 'Needs Repair',
          className:
            'bg-rose-500/10 text-rose-400 border border-rose-500/20 px-3 py-1 rounded-full text-[11px] font-semibold',
        };
      default:
        return {
          text: mapApiConditionToUi(condition),
          className:
            'bg-zinc-800 text-zinc-300 border border-zinc-700 px-3 py-1 rounded-full text-[11px] font-semibold',
        };
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Top Filter and Actions Row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Left: Filters */}
        <div className="flex items-center gap-2 overflow-visible">
          {/* Condition Filter */}
          <div className="relative" ref={conditionRef}>
            <button
              onClick={() => setIsConditionFilterOpen(!isConditionFilterOpen)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-[12px] font-semibold transition-all duration-200 cursor-pointer ${
                selectedConditions.length > 0
                  ? 'bg-blue-600/10 border-blue-500 text-blue-400'
                  : 'border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-200 bg-zinc-900/50'
              }`}
            >
              <span>Items</span>
              {selectedConditions.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-blue-500 text-white text-[9px] font-bold leading-none">
                  {selectedConditions.length}
                </span>
              )}
              <FiChevronDown
                size={12}
                className={`transition-transform duration-200 shrink-0 ${
                  isConditionFilterOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            <AnimatePresence>
              {isConditionFilterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.98 }}
                  transition={{ duration: 0.1 }}
                  className="absolute left-0 mt-2 z-50 bg-[#141414] border border-white/10 rounded-2xl p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.5)] min-w-[160px]"
                >
                  {['Excellent', 'Good', 'Fair', 'Needs Repair'].map((cond) => {
                    const isSelected = selectedConditions.includes(cond);
                    return (
                      <button
                        key={cond}
                        onClick={() => toggleConditionFilter(cond)}
                        className={`w-full text-left px-3 py-2.5 rounded-xl text-xs transition-all flex items-center justify-between cursor-pointer ${
                          isSelected
                            ? 'bg-[#0B2545] text-[#60A5FA] font-semibold'
                            : 'text-zinc-300 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <span>{cond}</span>
                        {isSelected && <FiCheck className="w-3.5 h-3.5 text-[#60A5FA]" />}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Category Filter */}
          <div className="relative" ref={categoryRef}>
            <button
              onClick={() => setIsCategoryFilterOpen(!isCategoryFilterOpen)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-[12px] font-semibold transition-all duration-200 cursor-pointer ${
                selectedCategories.length > 0
                  ? 'bg-blue-600/10 border-blue-500 text-blue-400'
                  : 'border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-200 bg-zinc-900/50'
              }`}
            >
              <span>Category</span>
              {selectedCategories.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-blue-500 text-white text-[9px] font-bold leading-none">
                  {selectedCategories.length}
                </span>
              )}
              <FiChevronDown
                size={12}
                className={`transition-transform duration-200 shrink-0 ${
                  isCategoryFilterOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            <AnimatePresence>
              {isCategoryFilterOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.98 }}
                  transition={{ duration: 0.1 }}
                  className="absolute left-0 mt-2 z-50 bg-[#141414] border border-white/10 rounded-2xl p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.5)] min-w-[200px] max-h-60 overflow-y-auto no-scrollbar"
                >
                  {uniqueCategories.length === 0 ? (
                    <div className="px-3 py-2 text-zinc-500 text-xs text-center">
                      No categories found
                    </div>
                  ) : (
                    uniqueCategories.map((cat) => {
                      const isSelected = selectedCategories.includes(cat);
                      return (
                        <button
                          key={cat}
                          onClick={() => toggleCategoryFilter(cat)}
                          className={`w-full text-left px-3 py-2.5 rounded-xl text-xs transition-all flex items-center justify-between cursor-pointer ${
                            isSelected
                              ? 'bg-[#0B2545] text-[#60A5FA] font-semibold'
                              : 'text-zinc-300 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <span>{cat}</span>
                          {isSelected && <FiCheck className="w-3.5 h-3.5 text-[#60A5FA]" />}
                        </button>
                      );
                    })
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right: Actions */}
        {isOwn && (
          <button
            onClick={() => setIsAddOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#18181B] hover:bg-[#27272A] border border-white/10 text-white rounded-full text-xs font-semibold hover:border-white/20 transition-all cursor-pointer shadow-sm"
          >
            <FiPlus size={14} />
            Add Item
          </button>
        )}
      </div>

      {/* Grid of Items */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className="h-44 rounded-[24px] bg-[#111111] border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4 text-center rounded-2xl border border-white/5 bg-[#111111]">
          <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-600">
            <FiBox size={28} />
          </div>
          <div>
            <p className="text-white text-lg font-semibold">No Items Yet</p>
            <p className="text-zinc-500 text-sm mt-1">Track equipment and supplies here</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => {
            const badge = getStatusBadge(item.condition);
            return (
              <div
                key={item.uuid}
                className="bg-[#111111] border border-white/8 hover:border-white/15 transition-all duration-300 rounded-[24px] p-5 flex flex-col justify-between group relative overflow-hidden"
              >
                <div>
                  {/* Top: Icon + Badge */}
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-xl bg-zinc-800/60 border border-white/5 flex items-center justify-center text-zinc-400">
                      <FiBox size={18} />
                    </div>
                    <span className={badge.className}>{badge.text}</span>
                  </div>

                  {/* Content */}
                  <div className="mt-4">
                    <h4 className="text-white font-bold text-[15px] truncate" title={item.name}>
                      {item.name}
                    </h4>
                    <p className="text-zinc-500 text-[12px] mt-0.5">
                      {item.category_label || mapApiCategoryToUi(item.category)} · Qty:{' '}
                      {item.quantity}
                    </p>
                  </div>
                </div>

                {/* Bottom Row (Pencil + Trash) */}
                {isOwn && (
                  <div className="flex items-center justify-end gap-1 mt-4">
                    <button
                      onClick={() => handleOpenEdit(item)}
                      className="p-1.5 rounded-lg text-zinc-500 hover:text-white transition-colors cursor-pointer"
                    >
                      <FiEdit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleOpenDelete(item)}
                      className="p-1.5 rounded-lg text-red-500 hover:text-red-400 transition-colors cursor-pointer"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {isOwn && (
        <>
          <AddInventoryItemModal
            isOpen={isAddOpen}
            onClose={() => setIsAddOpen(false)}
            onSubmit={handleAddSubmit}
            itemName={itemName}
            setItemName={setItemName}
            itemCategory={itemCategory}
            setItemCategory={setItemCategory}
            isCategoryDropdownOpen={isCategoryDropdownOpen}
            setIsCategoryDropdownOpen={setIsCategoryDropdownOpen}
            itemAssetTag={itemAssetTag}
            setItemAssetTag={setItemAssetTag}
            itemQuantity={itemQuantity}
            setItemQuantity={setItemQuantity}
            itemBudgetAllocation={itemBudgetAllocation}
            setItemBudgetAllocation={setItemBudgetAllocation}
            itemLocation={itemLocation}
            setItemLocation={setItemLocation}
            itemCondition={itemCondition}
            setItemCondition={setItemCondition}
            isSubmitting={isCreating}
            error={addError}
          />

          <EditInventoryItemModal
            isOpen={isEditOpen}
            editingInventoryItem={editingItem}
            onClose={() => setIsEditOpen(false)}
            onSubmit={handleEditSubmit}
            editItemName={editItemName}
            setEditItemName={setEditItemName}
            editItemCategory={editItemCategory}
            setEditItemCategory={setEditItemCategory}
            isEditCategoryDropdownOpen={isEditCategoryDropdownOpen}
            setIsEditCategoryDropdownOpen={setIsEditCategoryDropdownOpen}
            editItemAssetTag={editItemAssetTag}
            setEditItemAssetTag={setEditItemAssetTag}
            editItemQuantity={editItemQuantity}
            setEditItemQuantity={setEditItemQuantity}
            editItemBudgetAllocation={editItemBudgetAllocation}
            setEditItemBudgetAllocation={setEditItemBudgetAllocation}
            editItemLocation={editItemLocation}
            setEditItemLocation={setEditItemLocation}
            editItemCondition={editItemCondition}
            setEditItemCondition={setEditItemCondition}
            isSubmitting={isUpdating}
            error={editError}
          />

          {/* Delete modal exactly matching the screenshot */}
          <AnimatePresence>
            {isDeleteOpen && deletingItem && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={isDeleting ? undefined : () => setIsDeleteOpen(false)}
                  className="absolute inset-0 bg-black/80 backdrop-blur-md"
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="relative w-full max-w-md bg-[#0F0F0F] border border-white/10 rounded-[32px] p-6 overflow-hidden z-10 text-center shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
                >
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={() => setIsDeleteOpen(false)}
                      disabled={isDeleting}
                      className="p-1.5 rounded-full hover:bg-white/5 text-zinc-400 hover:text-white cursor-pointer transition-colors disabled:opacity-50"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="w-12 h-12 rounded-full bg-[#3B1216] border border-[#DC2626]/30 text-[#F87171] flex items-center justify-center mx-auto mb-4">
                    <FiTrash2 className="w-5 h-5" />
                  </div>

                  <h3 className="text-lg font-bold text-white mb-1">Delete Inventory Item?</h3>
                  <p className="text-zinc-500 text-xs px-4 mb-4">
                    This schedule entry will be permanently removed from the run of show.
                  </p>

                  <div className="bg-[#291B00] border border-[#D97706]/40 text-[#FBBF24] text-xs p-3.5 rounded-2xl flex items-center gap-3 text-left my-4">
                    <FiAlertTriangle className="w-5 h-5 text-[#FBBF24] shrink-0" />
                    <span>
                      This action cannot be undone. The item will be removed from the project
                      inventory permanently.
                    </span>
                  </div>

                  {/* Card preview */}
                  <div className="bg-[#141414] border border-white/5 rounded-2xl p-3.5 flex items-center gap-3.5 text-left mb-6">
                    <div className="w-9 h-9 rounded-xl bg-zinc-900 border border-white/5 flex items-center justify-center text-zinc-400 shrink-0">
                      <FiBox className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-white text-xs font-bold">{deletingItem.name}</h4>
                      <p className="text-zinc-500 text-[11px] mt-0.5">
                        {deletingItem.category_label || mapApiCategoryToUi(deletingItem.category)} ·
                        Qty: {deletingItem.quantity}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setIsDeleteOpen(false)}
                      disabled={isDeleting}
                      className="w-1/2 py-2.5 border border-white/15 hover:bg-white/5 text-white rounded-full text-xs font-semibold transition-all cursor-pointer disabled:opacity-50"
                    >
                      Keep Item
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmDelete}
                      disabled={isDeleting}
                      className="w-1/2 py-2.5 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 rounded-full text-xs font-bold transition-all cursor-pointer active:scale-95 flex items-center justify-center gap-2"
                    >
                      {isDeleting ? (
                        <>
                          <span className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <span>Delete Item</span>
                      )}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
};
